import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Upload, FileText, Trash2, Download, Eye, X, Plus, File, Image, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = window.location.origin;

const TIPO_ICONS = {
  foto: Image, evidencia: Image, certificado: FileText, certificado_classe: FileText,
  laudo: FileText, vistoria: FileText, ordem_servico: FileText, relatorio: FileText,
  atestado_medico: AlertTriangle, cir: FileText, aso: FileText, ctps: FileText,
};

const TIPO_LABELS = {
  cir: 'CIR', aso: 'ASO', ctps: 'CTPS', certificado: 'Certificado', rg: 'RG', cpf: 'CPF', foto: 'Foto',
  laudo: 'Laudo', certificado_classe: 'Certificado Classe', vistoria: 'Vistoria', seguro: 'Seguro', registro: 'Registro',
  evidencia: 'Evidencia', acao_corretiva: 'Acao Corretiva', relatorio: 'Relatorio',
  ordem_servico: 'Ordem de Servico', checklist: 'Checklist',
  atestado_medico: 'Atestado Medico', certificado_curso: 'Certificado Curso',
  documento_afastamento: 'Doc. Afastamento', comprovante: 'Comprovante', outros: 'Outros',
};

const fmtSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
};

export function DocumentUploadPanel({ modulo, entidadeId, entidadeNome, compact = false }) {
  const { token, hasRole } = useAuth();
  const isAdmin = hasRole(['admin', 'gestor', 'operador']);
  const [docs, setDocs] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [tipoSel, setTipoSel] = useState('outros');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchDocs = useCallback(async () => {
    if (!entidadeId) return;
    setLoading(true);
    try {
      const endpoint = modulo === 'tripulantes'
        ? `${API}/api/documentos/tripulantes/${entidadeId}`
        : `${API}/api/documentos/${modulo}/${entidadeId}`;
      const r = await fetch(endpoint, { headers });
      setDocs(await r.json());
      const tR = await fetch(`${API}/api/documentos/tipos/${modulo}`, { headers });
      const tData = await tR.json();
      setTipos(tData.tipos || []);
    } catch {}
    finally { setLoading(false); }
  }, [token, modulo, entidadeId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Arquivo excede 10MB'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const endpoint = modulo === 'tripulantes'
        ? `${API}/api/tripulantes/${entidadeId}/upload/${tipoSel}`
        : `${API}/api/documentos/upload/${modulo}/${entidadeId}?tipo_documento=${tipoSel}`;
      const r = await fetch(endpoint, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData });
      if (r.ok) {
        toast.success('Documento enviado');
        setShowUpload(false);
        fetchDocs();
      } else {
        const err = await r.json();
        toast.error(err.detail || 'Erro no upload');
      }
    } catch { toast.error('Erro no upload'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Remover documento?')) return;
    await fetch(`${API}/api/documentos/${docId}`, { method: 'DELETE', headers });
    toast.success('Removido');
    fetchDocs();
  };

  if (!entidadeId) return null;

  return (
    <div className={`bg-slate-900/60 border border-slate-800 rounded-lg overflow-hidden ${compact ? '' : 'mt-3'}`} data-testid={`docs-panel-${modulo}`}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800 bg-slate-800/30">
        <h4 className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
          <FileText className="w-3.5 h-3.5 text-cyan-400" />
          Documentos {entidadeNome ? `- ${entidadeNome}` : ''} <span className="text-slate-500">({docs.length})</span>
        </h4>
        {isAdmin && (
          <Button size="sm" variant="outline" onClick={() => setShowUpload(!showUpload)} className="h-6 text-[10px] px-2" data-testid="btn-upload-doc">
            <Upload className="w-3 h-3 mr-1" />{showUpload ? 'Fechar' : 'Enviar'}
          </Button>
        )}
      </div>

      {showUpload && isAdmin && (
        <div className="px-3 py-2 border-b border-slate-700 bg-slate-800/20 flex items-center gap-2 flex-wrap">
          <select value={tipoSel} onChange={e => setTipoSel(e.target.value)} data-testid="select-tipo-doc"
            className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white">
            {tipos.map(t => <option key={t} value={t}>{TIPO_LABELS[t] || t}</option>)}
          </select>
          <label className={`flex items-center gap-1 px-3 py-1 rounded text-xs cursor-pointer transition ${uploading ? 'bg-slate-700 text-slate-500' : 'bg-cyan-600 hover:bg-cyan-500 text-white'}`}>
            <Plus className="w-3 h-3" />{uploading ? 'Enviando...' : 'Selecionar Arquivo'}
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploading}
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx" data-testid="input-file" />
          </label>
          <span className="text-[10px] text-slate-500">Max: 10MB | PDF, JPG, PNG, DOC, XLS</span>
        </div>
      )}

      {loading ? (
        <div className="p-4 text-center text-xs text-slate-500">Carregando...</div>
      ) : docs.length === 0 ? (
        <div className="p-4 text-center text-xs text-slate-500">Nenhum documento enviado</div>
      ) : (
        <div className="divide-y divide-slate-800/50 max-h-60 overflow-y-auto">
          {docs.map((d, i) => {
            const IconComp = TIPO_ICONS[d.tipo_documento] || File;
            return (
              <div key={d.id || i} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-800/30 transition" data-testid={`doc-${i}`}>
                <IconComp className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-white truncate">{d.nome_arquivo}</p>
                  <p className="text-[10px] text-slate-500">
                    <span className="px-1 py-0 rounded bg-slate-800 text-slate-400 mr-1">{TIPO_LABELS[d.tipo_documento] || d.tipo_documento}</span>
                    {fmtSize(d.tamanho_bytes)} | {d.enviado_por} | {d.enviado_em?.slice(0, 10)}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <a href={`${API}${d.arquivo_url}`} target="_blank" rel="noopener noreferrer" className="p-1 hover:bg-slate-700 rounded" title="Visualizar">
                    <Eye className="w-3 h-3 text-cyan-400" />
                  </a>
                  <a href={`${API}${d.arquivo_url}${d.arquivo_url?.includes('?') ? '&' : '?'}download=${encodeURIComponent(d.nome_arquivo || 'documento')}`} className="p-1 hover:bg-slate-700 rounded" title="Baixar" data-testid={`btn-download-${i}`}>
                    <Download className="w-3 h-3 text-slate-400" />
                  </a>
                  {hasRole(['admin']) && (
                    <button onClick={() => handleDelete(d.id)} className="p-1 hover:bg-red-900/30 rounded" title="Remover">
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default DocumentUploadPanel;
